import { Alert, Box, Checkbox, Chip, FormControlLabel, Grid, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { api } from '@/api/client'
import type { PackageItem } from '@/api/types'

/** One dish picked off the package for a sitting. */
export type MenuChoice = {
  menuItemId: string
  variantId?: string
  packageItemId: string
  liveCooking: boolean
}

/**
 * What the customer chooses off the package, for one sitting.
 *
 * <p>A package is a set of lines - a starter, two curries, a sweet - and each
 * line says how many of its dishes may be taken. The dishes it always includes
 * are shown ticked and cannot be unticked, because they are not a choice; the
 * rest are the choosing, and a line that is full closes off the ones nobody
 * picked rather than leaving them tickable for somebody to be told off about
 * afterwards.</p>
 *
 * <p>Nothing is written here. What comes back goes with the sitting when it is
 * saved, so the package, the rate and the dishes land together or not at all.</p>
 */
export function PackageMenuChoices({
  packageId,
  value,
  onChange,
}: {
  /** The package the sitting is sold on; nothing is asked without one. */
  packageId?: string
  value: MenuChoice[]
  onChange: (chosen: MenuChoice[]) => void
}) {
  const [rules, setRules] = useState<PackageItem[]>([])
  const [failed, setFailed] = useState(false)
  const [loadedFor, setLoadedFor] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!packageId) {
      setRules([])
      setLoadedFor(null)
      return
    }
    setFailed(false)
    api
      .get<PackageItem[]>(`/api/v1/packages/${packageId}/selection-rules`)
      .then((loaded) => {
        if (cancelled) return
        setRules(loaded)
        // Only when the package itself changes. Re-filling on every render
        // would undo what somebody had just unticked.
        if (loadedFor === packageId) return
        setLoadedFor(packageId)
        const start: MenuChoice[] = []
        for (const rule of loaded) {
          const mandatory = rule.choices.filter((choice) => choice.mandatory)
          // Filled to the minimum the line asks for, so a package opens on a
          // menu that is already valid and the office corrects it rather than
          // building it from nothing.
          const filler = rule.choices
            .filter((choice) => !choice.mandatory)
            .slice(0, Math.max(0, rule.minSelections - mandatory.length))
          for (const choice of [...mandatory, ...filler]) {
            start.push({
              menuItemId: choice.menuItemId,
              variantId: choice.variantId ?? undefined,
              packageItemId: rule.id,
              liveCooking: false,
            })
          }
        }
        onChange(start)
      })
      .catch(() => {
        if (!cancelled) {
          setRules([])
          setFailed(true)
        }
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageId])

  if (!packageId) {
    return null
  }
  if (failed) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        The dishes on this package could not be read. The sitting can still be saved; the menu can
        be picked afterwards on the Package &amp; items tab.
      </Alert>
    )
  }
  if (rules.length === 0) {
    return null
  }

  const chosenIn = (ruleId: string) => value.filter((one) => one.packageItemId === ruleId)

  const toggle = (ruleId: string, menuItemId: string, variantId: string | undefined, on: boolean) => {
    if (on) {
      onChange([...value, { menuItemId, variantId, packageItemId: ruleId, liveCooking: false }])
    } else {
      onChange(value.filter((one) => !(one.packageItemId === ruleId && one.menuItemId === menuItemId)))
    }
  }

  const setLive = (ruleId: string, menuItemId: string, live: boolean) =>
    onChange(
      value.map((one) =>
        one.packageItemId === ruleId && one.menuItemId === menuItemId
          ? { ...one, liveCooking: live }
          : one,
      ),
    )

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        What the customer is choosing
      </Typography>
      {rules.map((rule) => {
        const chosen = chosenIn(rule.id)
        const under = chosen.length < rule.minSelections
        const full = chosen.length >= rule.maxSelections
        return (
          <Box key={rule.id} sx={{ mb: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">
                <strong>Item {rule.itemNumber}</strong> · {rule.itemLabel}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                color={under ? 'warning' : 'default'}
                label={
                  rule.minSelections === rule.maxSelections
                    ? `${chosen.length} of ${rule.minSelections}`
                    : `${chosen.length} of ${rule.minSelections}–${rule.maxSelections}`
                }
              />
            </Stack>
            <Grid container>
              {rule.choices.map((choice) => {
                const picked = chosen.some((one) => one.menuItemId === choice.menuItemId)
                return (
                  <Grid item xs={12} sm={6} key={`${rule.id}-${choice.menuItemId}`}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FormControlLabel
                        sx={{ mr: 0, flexShrink: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={picked || choice.mandatory}
                            disabled={choice.mandatory || (full && !picked)}
                            onChange={(event) =>
                              toggle(
                                rule.id,
                                choice.menuItemId,
                                choice.variantId ?? undefined,
                                event.target.checked,
                              )
                            }
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {choice.name}
                            {choice.mandatory ? ' (always)' : ''}
                          </Typography>
                        }
                      />
                      {/* Offered only where the catalogue says the dish can be
                          done at a counter, and only once it is on the menu. */}
                      {picked && choice.liveCounter ? (
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={
                                chosen.find((one) => one.menuItemId === choice.menuItemId)
                                  ?.liveCooking ?? false
                              }
                              onChange={(event) =>
                                setLive(rule.id, choice.menuItemId, event.target.checked)
                              }
                            />
                          }
                          label={
                            <Typography variant="caption" color="text.secondary">
                              live
                            </Typography>
                          }
                        />
                      ) : null}
                    </Stack>
                  </Grid>
                )
              })}
            </Grid>
          </Box>
        )
      })}
    </Box>
  )
}
